<template>
  <Modal
    name="BULK_SEND_CONFIRMATION_EMAIL_MODAL"
    width="500"
    :title="$t('appointment.confirmAppointment.sendEmailModal.title')"
    ref="sendConfirmationEmails"
    actions
  >
    <template v-slot:body>
      <v-progress-linear
        class="mt-5 mb-7"
        v-show="isAppointmentLoading"
        indeterminate
        rounded
      />

      <div v-show="!isAppointmentLoading">
        <div v-show="!isSingleAppointment">
          <v-alert v-if="isUnableToSendConfirmation" class="red lighten-5 mt-2 mb-6">
            <p class="body-2 red--text mb-2">
              {{ $t('appointment.confirmAppointment.sendEmailModal.unableToSendEmail') }}
            </p>
            <v-chip v-for="(item, index) in getAppointmentsUnableToSendConfirmation" :key="index" class="mr-1 mb-1 red" x-small>
              <span class="white--text">
                {{ item.code }}
              </span>
            </v-chip>
            <p class="caption red--text mt-2">
              {{ $t('appointment.confirmAppointment.sendEmailModal.unableToSendEmailTip') }}
            </p>
          </v-alert>

          <p class="body-2 mb-2" v-if="canSendEmailConfirmations">
            {{ $t('appointment.confirmAppointment.sendEmailModal.sendEmailMessage', { quantity: getAppointmentsAbleToSendConfirmation.length }) }}
          </p>
          <v-chip v-for="(item, index) in getAppointmentsAbleToSendConfirmation" :key="index" class="mr-1 mb-1 orange" x-small>
            <span class="white--text">
              {{ item.code }}
            </span>
          </v-chip>
        </div>

        <!-- single appointment -->
        <ModalBulkSendConfirmationEmailSingleAppointment
          v-if="isSingleAppointment"
          :isUnableToSendSingleAppointmentConfirmation="isUnableToSendSingleAppointmentConfirmation"
          :appointmentUnableToSendConfirmation="appointmentUnableToSendConfirmation"
          :appointmentAbleToSendConfirmation="appointmentAbleToSendConfirmation"
        />
        <!-- /single appointment -->
      </div>
    </template>

    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        :disabled="loading"
        elevation="0"
        color="grey"
        class="mr-3"
        large
        text
      >
        {{ $t('appointment.confirmAppointment.sendEmailModal.cancel') }}
      </v-btn>
      <v-btn
        @click="sendConfirmationEmails"
        :loading="loading"
        :disabled="!canSendEmailConfirmations"
        elevation="0"
        large
      >
        {{ $t('appointment.confirmAppointment.sendEmailModal.send') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from 'vuex'
import { api } from '@/services'

import Modal from '../../app/Modal.vue'
import ModalBulkSendConfirmationEmailSingleAppointment from './fragments/ModalBulkSendConfirmationEmailSingleAppointment'

export default {
  name: 'ModalBulkSendConfirmationEmail',

  components: {
    Modal,
    ModalBulkSendConfirmationEmailSingleAppointment,
  },

  props: {
    selectedAppointments: {
      type: Array,
      required: true,
    },

    mapAction: {
      type: Boolean,
      required: false,
      default: true,
    }
  },

  watch: {
    appointment(appointment) {
      if (!appointment?.id) return

      if (this.canSendEmailConfirmation(appointment)) {
        this.appointmentAbleToSendConfirmation.push(appointment)
      } else {
        this.appointmentUnableToSendConfirmation.push(appointment)
      }
    }
  },

  computed: {
    ...mapGetters(['appointment', 'isAppointmentLoading']),

    isSingleAppointment() {
      return this.appointment && this.appointment.id
    },

    isUnableToSendConfirmation() {
      return this.getAppointmentsUnableToSendConfirmation && this.getAppointmentsUnableToSendConfirmation.length > 0
    },

    isUnableToSendSingleAppointmentConfirmation() {
      return this.appointmentUnableToSendConfirmation.length > 0
    },

    canSendEmailConfirmations() {
      if (this.isSingleAppointment) {
        return this.appointmentAbleToSendConfirmation.length > 0
      }

      return this.getAppointmentsAbleToSendConfirmation && this.getAppointmentsAbleToSendConfirmation.length > 0
    },

    getAppointmentsUnableToSendConfirmation() {
      return this.selectedAppointments.filter(appointment => !this.canSendEmailConfirmation(appointment))
    },

    getAppointmentsAbleToSendConfirmation() {
      return this.selectedAppointments.filter(appointment => this.canSendEmailConfirmation(appointment))
    },
  },

  methods: {
    ...mapActions(['showSuccessMessage', 'showErrorMessage']),
    ...mapMutations(['closeAllModals', 'setAppointment']),

    createPayload() {
      if (this.isSingleAppointment) {
        return {
          appointment_ids: [this.appointment.id],
        }
      }

      return {
        appointment_ids: this.getAppointmentsAbleToSendConfirmation.map(appointment => appointment.id),
      }
    },

    async sendConfirmationEmails() {
      this.loading = true

      try {
        await api.post('admin/appointment/sendConfirmationEmail', this.createPayload())
        this.showSuccessMessage(this.$t('appointment.confirmAppointment.sendEmailModal.successfullySent'))
        if (this.mapAction) {
          this.setAppointment({})
          this.$emit('refreshMap')
        }
        this.closeAllModals()
      } catch ({ data }) {
        this.showErrorMessage(data?.message || this.$t('appointment.confirmAppointment.sendEmailModal.error'))
      } finally {
        this.loading = false
      }
    },

    canSendEmailConfirmation(appointment) {
      return appointment.visitor_mail && ['DRAFT', 'OPEN', 'REJECTED', 'SCHEDULED'].includes(appointment.status)
    },

    resetSingleAppointmentConfirmation() {
      this.appointmentAbleToSendConfirmation = []
      this.appointmentUnableToSendConfirmation = []
    },
  },

  data() {
    return {
      loading: false,

      appointmentUnableToSendConfirmation: [],
      appointmentAbleToSendConfirmation: [],
    }
  },

  mounted() {
    if (this.mapAction) {
      this.$watch(
        () => this.$refs.sendConfirmationEmails.dialog,
        (isOpen) => {
          if (isOpen) {
            if (this.selectedAppointments >= 1) {
              this.resetSingleAppointmentConfirmation()
              this.setAppointment({})
            }
          }

          if(!isOpen) {
            this.resetSingleAppointmentConfirmation()
            this.setAppointment({})
          }
        }
      )
    }
  }
}
</script>
